import React, { useEffect, useState } from 'react'
import ReactGA from 'react-ga4'

import { useAppDispatch, useAppSelector } from '@/core/store'
import { addCleverMapPlayedObject, addPlayedAudio } from '@/core/store/catalog.store'
import { setModal } from '@/core/store/ui.store'

interface AudioPlayerProps {
  id: string
  file: string
  type: number //there may be several audios assigned to the same object
  autoPlaying: boolean
  excursion: string | undefined
  location?: {
    latitude: number
    longitude: number
  }
  applyCleverMapGate?: boolean
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({
  id,
  file,
  type,
  autoPlaying,
  excursion,
  location,
  applyCleverMapGate = true,
}) => {
  const [isPlaying, setIsPlaying] = useState(false)
  const audioRef = React.useRef<HTMLAudioElement | null>(null)

  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [playbackRate, setPlaybackRate] = useState(1)
  const [showSpeedMenu, setShowSpeedMenu] = useState(false)

  const userRealPosition = useAppSelector((store) => store.uiState.userRealPosition)

  const { playedAudioIds, hasCleverMapAccess, cleverMapPlayedObjectIds } = useAppSelector(
    (store) => store.catalogStore,
  )
  const dispatch = useAppDispatch()

  const isObjectAlreadyPlayed = cleverMapPlayedObjectIds.includes(id)

  const cleverMapPlaybackBlocked = () => {
    if (!applyCleverMapGate) return false
    if (hasCleverMapAccess === true) return false
    if (isObjectAlreadyPlayed) return false
    return cleverMapPlayedObjectIds.length >= 3
  }

  const onTogglePlayClick = () => {
    if (isPlaying) {
      setIsPlaying(false)
      return
    }
    if (cleverMapPlaybackBlocked()) {
      dispatch(setModal({ name: 'clever_map_access' }))
      return
    }
    setIsPlaying(true)
  }

  const calculateDistance = () => {
    if (!location || !userRealPosition) return null

    const R = 6371e3 // Earth's radius in meters
    const φ1 = (userRealPosition.latitude * Math.PI) / 180
    const φ2 = (location.latitude * Math.PI) / 180
    const Δφ = ((location.latitude - userRealPosition.latitude) * Math.PI) / 180
    const Δλ = ((location.longitude - userRealPosition.longitude) * Math.PI) / 180

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

    const distance = R * c
    return Math.round(distance)
  }

  const handlePlay = () => {
    if (applyCleverMapGate && hasCleverMapAccess !== true) {
      const alreadyPlayed = cleverMapPlayedObjectIds.includes(id)
      if (!alreadyPlayed && cleverMapPlayedObjectIds.length >= 3) {
        audioRef.current?.pause()
        setIsPlaying(false)
        dispatch(setModal({ name: 'clever_map_access' }))
        return
      }
      if (!alreadyPlayed) {
        dispatch(addCleverMapPlayedObject(id))
      }
    }

    Array.from(document.querySelectorAll('audio')).forEach((audio) => {
      if (id !== audio.getAttribute('data-id')) {
        if (!audio.paused) audio.pause()
      }
    })

    {
      ReactGA.event({
        category: 'play',
        action: 'WA_AudioPlayed',
        label: id,
        value: playedAudioIds.length + 1,
      })
    }

    dispatch(addPlayedAudio(id))
  }

  const handlePause = () => {
    if (audioRef.current) {
      setIsPlaying(false)
      if (!audioRef.current.paused) audioRef.current.pause()
    }
    ReactGA.event({
      category: 'play',
      action: 'WA_AudioPaused',
      label: id,
    })
  }

  const handleEnded = () => {
    if (audioRef.current) {
      setIsPlaying(false)
      if (!audioRef.current.paused) audioRef.current.pause()
    }
    ReactGA.event({
      category: 'play',
      action: 'WA_AudioEnded',
      label: id,
    })
  }

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    // Сброс состояния при изменении src
    setIsPlaying(false)
    setCurrentTime(0)
    setDuration(0)
    setError(null)

    if (file) {
      // audio.src = src;
      audio.load()
    }
    // else {
    //   audio.removeAttribute('src');
    // }
  }, [file])

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    audio.playbackRate = playbackRate

    if (isPlaying) {
      audio.play().catch((err) => {
        console.error('Audio playback error:', err)
        setError(`Playback failed: ${err.message}`)
        setIsPlaying(false)
      })
    } else {
      audio.pause()
    }
  }, [isPlaying, playbackRate])

  // Обработчики событий аудио
  const handleError = () => {
    const audio = audioRef.current
    if (!audio) return

    let errorMessage = 'Unknown audio error'

    switch (audio.error?.code) {
      case MediaError.MEDIA_ERR_ABORTED:
        errorMessage = 'Playback was aborted'
        break
      case MediaError.MEDIA_ERR_NETWORK:
        errorMessage = 'Network error'
        break
      case MediaError.MEDIA_ERR_DECODE:
        errorMessage = 'Decoding error'
        break
      case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
        errorMessage = 'The audio source is not supported or not found'
        break
    }

    console.error('Audio loading error:', errorMessage)
    setError(errorMessage)
    setIsPlaying(false)
  }

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime)
    }
  }

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration)
      setError(null)
    }
  }

  const handleProgressBarClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current
    if (!audio) return

    const progressBar = e.currentTarget
    const clickPosition = e.clientX - progressBar.getBoundingClientRect().left
    const progressBarWidth = progressBar.clientWidth
    const seekTime = (clickPosition / progressBarWidth) * duration

    setCurrentTime(seekTime)
    audio.currentTime = seekTime
  }

  const formatTime = (timeInSeconds: number) => {
    const minutes = Math.floor(timeInSeconds / 60)
    const seconds = Math.floor(timeInSeconds % 60)
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
  }

  const changePlaybackRate = (rate: number) => {
    setPlaybackRate(rate)
    setShowSpeedMenu(false)
  }

  if (!file) {
    return <div>No audio file .</div>
  }

  if (autoPlaying) {
    return (
      <div className="audio-player">
        <audio
          data-id={id}
          preload={'none'}
          ref={audioRef}
          controls={true}
          autoPlay={true}
          controlsList={'nofullscreen nodownload '}
          src={file}
          onPause={handlePause}
          onPlay={handlePlay}
          onEnded={handleEnded}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onError={handleError}
        />
        <button
          className={`play-button ${isPlaying ? 'pause' : 'play'}`}
          onClick={onTogglePlayClick}
        ></button>

        <div className="progress-container" onClick={handleProgressBarClick}>
          <div className="progress-bar" style={{ width: `${(currentTime / duration) * 100}%` }} />
        </div>

        <div className="time-display">
          <span>{formatTime(currentTime)}</span>
          <span> / </span>
          <span>{formatTime(duration)}</span>
        </div>
      </div>
    )
  } else {
    return (
      <div className="audio-player">
        <audio
          data-id={id}
          preload={'none'}
          ref={audioRef}
          controls={true}
          controlsList={'nofullscreen nodownload '}
          src={file}
          onPause={handlePause}
          onPlay={handlePlay}
          onEnded={handleEnded}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onError={handleError}
        />
        <div className="player-controls">
          <button
            className={`play-button ${isPlaying ? 'pause' : 'play'}`}
            onClick={onTogglePlayClick}
            aria-label={isPlaying ? 'Pause' : 'Play'}
          />

          <div className="progress-container" onClick={handleProgressBarClick}>
            <div className="progress-bar" style={{ width: `${(currentTime / duration) * 100}%` }} />
            <div
              className="progress-thumb"
              style={{ left: `${(currentTime / duration) * 100}%` }}
            />
          </div>

          <div className="time-display">
            <span>{formatTime(currentTime)}</span>
            <span> / </span>
            <span>{formatTime(duration)}</span>
          </div>

          <div className="speed-control">
            <button
              className="speed-button"
              onClick={() => setShowSpeedMenu(!showSpeedMenu)}
              aria-label="Playback speed"
            >
              {playbackRate}x
            </button>

            {showSpeedMenu && (
              <div className="speed-menu">
                {[0.5, 0.75, 1, 1.25, 1.5, 2].map((rate) => (
                  <button
                    key={rate}
                    className={`speed-option ${playbackRate === rate ? 'active' : ''}`}
                    onClick={() => changePlaybackRate(rate)}
                  >
                    {rate}x
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }
}

export default AudioPlayer
